import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { FeedPathTag } from "@/components/FeedPathTag";
import { VersionTag } from "@/components/VersionTag";
import { SyncFieldBadge } from "@/components/SyncFieldBadge";
import { Pencil, Save } from "lucide-react";

function Field({ label, required, feedPath, syncPath, editing, ddmMigrated, syncedFields, className = "", children }) {
  return (
    <div className={className}>
      <Label className="data-label mb-1 flex items-center gap-1">
        {label}
        {required && <span className="text-destructive">*</span>}
        <FeedPathTag path={feedPath} />
        <SyncFieldBadge path={syncPath} editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields} />
      </Label>
      {children}
    </div>
  );
}

/**
 * Main Contact card. Owns its own local edit state; commits via PATCH and
 * bubbles the fresh dealer document up via `onSaved`.
 */
// `canManage` is dealer.main_contact.edit for THIS dealer. `isMhq` splits
// the field set on top of it: name/telephone/fax come from the DDM feed and
// stay view-only for dealer-side users, matching the backend, which silently
// drops those fields for a non-MHQ caller (server.py update_main_contact).
export function MainContactCard({ dealer, dealerId, onSaved, syncedFields = [], canManage = false, isMhq = false }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft({ ...(dealer.main_contact || {}) });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/dealers/${dealerId}/main-contact`, draft);
      onSaved(data);
      setEditing(false);
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const v = (k) => (editing ? draft?.[k] ?? "" : dealer.main_contact?.[k] || "");

  return (
    <Card className="mb-6 p-6" data-testid="main-contact-card">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="flex items-center gap-1.5 text-lg font-semibold">{t("dealership.mainContact")} <VersionTag version={dealer.card_versions?.vps} testId="version-tag-vps" /></h2>
          <p className="mt-1 text-xs text-muted-foreground">{t("dealership.mainContactSub")}</p>
        </div>
        {!editing ? (
          canManage && (
            <Button data-testid="edit-contact-btn" variant="ghost" size="sm" className="gap-1.5" onClick={start}>
              <Pencil className="h-3.5 w-3.5" />
              {t("common.edit")}
            </Button>
          )
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              {t("common.cancel")}
            </Button>
            <Button data-testid="save-contact-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {t("common.save")}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t("dealership.name")} required feedPath="Dealer.Name" syncPath="name"
          editing={editing} ddmMigrated={dealer.management_migrated_to_ddm === true} syncedFields={syncedFields}>
          <Input data-testid="contact-name" disabled={!editing || saving || !canManage || !isMhq} value={v("name")}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        <Field label={t("dealership.email")} required feedPath="Dealer.MainContact.Mail" syncPath="main_contact.email"
          editing={editing} ddmMigrated={dealer.management_migrated_to_ddm === true} syncedFields={syncedFields}>
          <Input data-testid="contact-email" disabled={!editing || saving || !canManage} value={v("email")}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
        </Field>
        <Field label={t("dealership.telephone")} required feedPath="Dealer.MainContact.Telephone" syncPath="main_contact.telephone"
          editing={editing} ddmMigrated={dealer.management_migrated_to_ddm === true} syncedFields={syncedFields}>
          <Input data-testid="contact-telephone" disabled={!editing || saving || !canManage || !isMhq} value={v("telephone")}
            onChange={(e) => setDraft({ ...draft, telephone: e.target.value })} />
        </Field>
        <Field label={t("dealership.fax")} feedPath="Dealer.MainContact.Fax" syncPath="main_contact.fax"
          editing={editing} ddmMigrated={dealer.management_migrated_to_ddm === true} syncedFields={syncedFields}>
          <Input data-testid="contact-fax" disabled={!editing || saving || !canManage || !isMhq} value={v("fax")}
            onChange={(e) => setDraft({ ...draft, fax: e.target.value })} />
        </Field>
      </div>
    </Card>
  );
}
